import { useState, useEffect, useRef } from 'react';
import { PERMISSIONS_UPDATED_EVENT } from '../constants/modules';

// ── What may the signed-in user do? ─────────────────────────────────────────
// The answer comes from the server (GET /api/access/me): the user's role, resolved
// from the database, as a map of capability → true | 'own' | 'all'. The UI uses it
// to hide what a person cannot use; the SERVER is what actually enforces it.
//
//   const { can, scope, isAdmin } = usePermissions();
//   can('tasks.edit')           // new style: one capability
//   scope('tasks.view')         // 'all' | 'own' | null
//   can('clients')              // legacy: may this user open the Clients module?
//   can('clients', 'delete')    // legacy: does the role hold ANY delete-type capability there?
//
// The legacy two-argument form keeps older screens (and the mobile apps) working
// unchanged while they are moved to explicit capabilities.

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
let cache = null;          // { token, data, version, at }
let inflight = null;

const classify = (key) => {
  const s = key.split('.').pop();
  if (/(^|_)view$/.test(s) || (key.startsWith('reports.') && s !== 'export')) return 'view';
  if (/(^|_)create$/.test(s)) return 'create';
  if (/(^|_)edit$/.test(s)) return 'edit';
  if (/(^|_)delete$/.test(s)) return 'delete';
  if (/(^|_)export$/.test(s) || s === 'send') return 'export';
  return 'other';
};

async function fetchAccess() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  if (cache && cache.token === token) return cache.data;
  if (inflight && inflight.token === token) return inflight.promise;
  let version = null;
  const promise = fetch(`${BASE}/api/access/me`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => { version = r.headers.get('X-Access-Version'); return r.ok ? r.json() : null; })
    .then(d => { if (d) cache = { token, data: d, version, at: Date.now() }; return d; })
    .catch(() => null)
    .finally(() => { inflight = null; });
  inflight = { token, promise };
  return promise;
}

// Call after an admin edits roles, or after login/logout, so every open screen re-reads.
export function invalidatePermissionCache() {
  cache = null;
  window.dispatchEvent(new CustomEvent(PERMISSIONS_UPDATED_EVENT));
}

// Every API response carries X-Access-Version — a fingerprint of the user's current role
// (ca-backend/middleware/auth.js). When it no longer matches what we loaded, an admin has
// changed this user's role: re-read it so buttons and menus update without a refresh.
// Called from the global fetch wrapper (utils/accessFeedback.js).
export function noteAccessVersion(version) {
  if (!version || !cache || !cache.version || inflight) return;
  if (version !== cache.version) invalidatePermissionCache();
}

// Coming back to the tab after a while: re-check, in case nothing else calls the API.
if (typeof window !== 'undefined') {
  const recheck = () => {
    if (document.visibilityState === 'hidden' || !cache) return;
    if (Date.now() - (cache.at || 0) > 30000) invalidatePermissionCache();
  };
  window.addEventListener('focus', recheck);
  document.addEventListener('visibilitychange', recheck);
  // and quietly every 2 minutes while the tab is open
  setInterval(recheck, 120000);
}

function makeHelpers(data) {
  const isAdmin = !!data?.isAdmin;
  const caps = data?.caps || {};
  const has = (k) => isAdmin || Object.prototype.hasOwnProperty.call(caps, k);
  const scope = (k) => {
    if (isAdmin) return 'all';
    if (!Object.prototype.hasOwnProperty.call(caps, k)) return null;
    return caps[k] === 'own' ? 'own' : 'all';
  };
  const can = (a, b) => {
    if (isAdmin) return true;
    if (!data) return false;
    if (a && a.includes('.')) return has(a);            // new style: 'tasks.edit'
    // legacy: can('clients') / can('clients','delete')
    if (!has(`${a}.access`)) return false;
    if (!b || b === 'view') return true;
    // '<module>.access' only opens the module — it must not count as an edit right.
    return Object.keys(caps).some(k => k.startsWith(a + '.') && !k.startsWith('dashboard.widget.') && k !== `${a}.access`
      && (classify(k) === b || (b === 'edit' && classify(k) === 'other')));
  };
  const canAny = (...keys) => keys.some(k => can(k));
  return { can, canAny, scope, isAdmin };
}

export function usePermissions() {
  const stored = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const [data, setData] = useState(() => (cache && cache.token === localStorage.getItem('token') ? cache.data : null));
  const [loading, setLoading] = useState(!data);
  const [tick, setTick] = useState(0);
  // Once permissions are known, a background refresh (tab refocus, admin edit) must
  // not flip us back to 'loading': screens gate their buttons — and some their whole
  // modals — on !loading, so that flicker used to unmount an open form and wipe
  // what the user had typed.
  const hasData = useRef(!!data);
  hasData.current = !!data;
  const token = localStorage.getItem('token');

  useEffect(() => {
    const bump = () => setTick(t => t + 1);
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, bump);
    return () => window.removeEventListener(PERMISSIONS_UPDATED_EVENT, bump);
  }, []);

  useEffect(() => {
    let live = true;
    if (cache && cache.token === token) { setData(cache.data); setLoading(false); return () => { live = false; }; }
    if (!hasData.current) setLoading(true);
    fetchAccess().then(d => { if (live) { if (d) setData(d); setLoading(false); } });
    return () => { live = false; };
  }, [token, tick]);

  const h = makeHelpers(data);
  const isAdmin = data ? h.isAdmin : (stored.is_admin === true || stored.role === 'admin');
  return {
    can: h.can, canAny: h.canAny, scope: h.scope, isAdmin, loading,
    // 'admin' | 'staff' — all legacy code ever needs to know is whether the user is the admin
    role: isAdmin ? 'admin' : 'staff',
    roleName: data?.role?.name || stored.role_name || null,
    caps: data?.caps || {},
    perms: {},
  };
}
